// Vendor Team (HENT staff) yönetimi
import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { Button, Modal, FormField, EmptyState, ListPageHeader } from '../components/ui';

const fmtDate = (s) => s ? new Date(s).toLocaleString('tr-TR') : '—';

export default function VendorTeamPage() {
  const [list, setList] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [pwUser, setPwUser] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, r] = await Promise.all([api.get('/vendor/team'), api.get('/vendor/vendor-roles')]);
      setList(t.data.data || []);
      setRoles(r.data.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id, name) => {
    if (!window.confirm(`"${name}" pasif edilsin mi?`)) return;
    await api.delete('/vendor/team/' + id);
    load();
  };

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Vendor Kullanıcı Tanımları (HENT Staff)"
        unit="kullanıcı"
        count={list.length}
        actions={<Button size="sm" onClick={() => setCreateOpen(true)}>+ Yeni Kullanıcı</Button>}
        onRefresh={load} refreshing={loading}
      />
      <div className="stocks-table-wrap">
        {loading ? <div style={{ padding: 40, textAlign:'center' }}>Yükleniyor...</div>
         : list.length === 0 ? <EmptyState title="Henüz vendor personeli yok" />
         : (
          <table className="stocks-table">
            <thead>
              <tr>
                <th>Ad</th><th>E-posta</th><th>Telefon</th><th>Rol</th>
                <th style={{ width: 140 }}>Son Giriş</th>
                <th style={{ width: 100 }}>Durum</th>
                <th style={{ width: 140 }}></th>
              </tr>
            </thead>
            <tbody>
              {list.map(u => (
                <tr key={u.id}>
                  <td><strong>{u.full_name}</strong><div style={{ fontSize:'0.72rem', color:'var(--text-tertiary)' }}>{u.username}</div></td>
                  <td>{u.email}</td>
                  <td style={{ fontFamily:'monospace', fontSize:'0.82rem' }}>{u.phone || '—'}</td>
                  <td><span style={{ fontSize:'0.75rem', padding:'3px 9px', borderRadius:100, background:'rgba(124,58,237,0.10)', color:'#7c3aed', fontWeight:600 }}>
                    {u.vendor_role_name || u.role}
                  </span></td>
                  <td style={{ fontSize:'0.78rem', color:'var(--text-tertiary)' }}>{fmtDate(u.last_login)}</td>
                  <td>{u.is_active
                    ? <span style={{ color:'var(--success)', fontWeight:600, fontSize:'0.78rem' }}>● Aktif</span>
                    : <span style={{ color:'var(--text-tertiary)', fontWeight:600, fontSize:'0.78rem' }}>● Pasif</span>}</td>
                  <td>
                    <Button size="sm" variant="ghost" onClick={() => setEditUser(u)}>Düzenle</Button>
                    <Button size="sm" variant="ghost" onClick={() => setPwUser(u)}>🔑 Şifre</Button>
                    {u.is_active && <Button size="sm" variant="ghost" onClick={() => remove(u.id, u.full_name)} style={{ color:'var(--danger)' }}>Pasif</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {(createOpen || editUser) && (
        <UserModal user={editUser} roles={roles}
                   onClose={() => { setCreateOpen(false); setEditUser(null); }}
                   onSaved={() => { setCreateOpen(false); setEditUser(null); load(); }} />
      )}
      {pwUser && (
        <PasswordModal user={pwUser}
                       onClose={() => setPwUser(null)}
                       onSaved={() => { setPwUser(null); load(); }} />
      )}
    </div>
  );
}

function PasswordModal({ user, onClose, onSaved }) {
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    setErr(null);
    if (pw.length < 6) return setErr('Şifre en az 6 karakter olmalı');
    if (pw !== pw2) return setErr('Şifreler eşleşmiyor');
    setBusy(true);
    try {
      await api.put('/vendor/team/' + user.id, { password: pw });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.message || 'Hata');
      setBusy(false);
    }
  };

  return (
    <Modal title={`Şifre Sıfırla — ${user.full_name}`} onClose={onClose}>
      <div style={{ padding: 18, display:'flex', flexDirection:'column', gap: 12 }}>
        <div style={{ fontSize: 12, color:'var(--text-tertiary)' }}>
          Kullanıcı: <strong>{user.username}</strong> ({user.email})
        </div>
        <FormField label="Yeni Şifre" required>
          <input className="ui-input" type="password" value={pw} onChange={e => setPw(e.target.value)} autoFocus />
        </FormField>
        <FormField label="Yeni Şifre (Tekrar)" required>
          <input className="ui-input" type="password" value={pw2} onChange={e => setPw2(e.target.value)} />
        </FormField>
        {err && <div style={{ color:'var(--danger)', fontSize: 13 }}>{err}</div>}
        <div style={{ display:'flex', gap: 8, justifyContent:'flex-end', marginTop: 8 }}>
          <Button variant="ghost" onClick={onClose}>İptal</Button>
          <Button variant="primary" onClick={submit} disabled={busy}>{busy ? '...' : 'Şifreyi Değiştir'}</Button>
        </div>
      </div>
    </Modal>
  );
}

function UserModal({ user, roles, onClose, onSaved }) {
  const editing = !!user;
  const [form, setForm] = useState({
    username: user?.username || '',
    email: user?.email || '',
    full_name: user?.full_name || '',
    phone: user?.phone || '',
    role: user?.role || 'vendor_support',
    is_active: user?.is_active ?? true,
    password: '',
  });
  const [busy, setBusy] = useState(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      if (editing) await api.put('/vendor/team/' + user.id, form);
      else await api.post('/vendor/team', form);
      onSaved();
    } catch (err) { alert(err.response?.data?.message || 'Hata'); setBusy(false); }
  };

  return (
    <Modal title={editing ? 'Personel Düzenle' : 'Yeni Personel'} onClose={onClose}>
      <div style={{ padding: 18, display:'flex', flexDirection:'column', gap: 12 }}>
        <FormField label="Ad Soyad" required><input className="ui-input" value={form.full_name} onChange={e => f('full_name', e.target.value)} /></FormField>
        <FormField label="E-posta" required><input className="ui-input" type="email" value={form.email} onChange={e => f('email', e.target.value)} disabled={editing} /></FormField>
        <FormField label="Telefon"><input className="ui-input" value={form.phone} onChange={e => f('phone', e.target.value)} /></FormField>
        <FormField label="Rol" required>
          <select className="ui-input" value={form.role} onChange={e => f('role', e.target.value)}>
            {roles.map(r => <option key={r.code} value={r.code}>{r.name}</option>)}
            <option value="super_admin">Super Admin (tam yetki)</option>
          </select>
        </FormField>
        <FormField label={editing ? 'Yeni Şifre (değiştirmek için)' : 'Şifre'} required={!editing}>
          <input className="ui-input" type="password" value={form.password} onChange={e => f('password', e.target.value)}
                 placeholder={editing ? 'Boş bırakırsanız değişmez' : ''} />
        </FormField>
        {editing && (
          <label style={{ display:'inline-flex', alignItems:'center', gap: 6 }}>
            <input type="checkbox" checked={form.is_active} onChange={e => f('is_active', e.target.checked)} /> Aktif
          </label>
        )}
        <div style={{ display:'flex', gap: 8, justifyContent:'flex-end', marginTop: 8 }}>
          <Button variant="ghost" onClick={onClose}>İptal</Button>
          <Button variant="primary" onClick={submit} disabled={busy}>{busy ? '...' : (editing ? 'Kaydet' : 'Oluştur')}</Button>
        </div>
      </div>
    </Modal>
  );
}
